import { AccountPanel, DeleteAccountSection } from './AccountPanel.jsx';
import { ThemeToggle } from './ThemeToggle.jsx';
import { HydrosAccountPanel } from './HydrosAccountPanel.jsx';
import { RingAccountPanel } from './RingAccountPanel.jsx';
import { DevicesAccountPanel } from './DevicesAccountPanel.jsx';
import { InvitePanel } from './InvitePanel.jsx';
import { useEffect, useRef } from 'react';
import { OPEN_FEEDBACK_EVENT } from './Feedback.jsx';

// Account-scoped settings: email and password, notifications, Siri,
// connected services, delete. A page since 2026-10-21 (owner: one long
// sheet), with a jump list to its parts; opened from the header, the side
// nav, the tanks grid and the tank menu.
const ACCOUNT_SECTIONS = [
  ['account-notifications', 'Notifications'],
  ['account-siri', 'Siri'],
  ['account-hydros', 'Connected services'],
  ['account-delete', 'Delete account'],
];

export function AccountSheet({ onClose, focus = null, linkTo = null, onSignOut = null }) {
  const hydrosRef = useRef(null);
  useEffect(() => {
    // 'hydros-link': back from Add a tank to link the new tank (feedback #69),
    // so it lands on the controller still on no tank, below the keys.
    // 'devices': a display's page, "Show another tank on it".
    if (focus === 'devices' || focus === 'ring') {
      const t = setTimeout(
        () =>
          document
            .getElementById(focus === 'ring' ? 'account-ring' : 'account-devices')
            ?.scrollIntoView({ block: 'start', behavior: 'smooth' }),
        400
      );
      return () => clearTimeout(t);
    }
    if (focus !== 'hydros' && focus !== 'hydros-link') return;
    // The HYDROS panel loads its status after mount and grows; scroll once
    // it has had a moment, so it does not land above the fold.
    const thing = linkTo?.thingId;
    const scroll = () => {
      const root = hydrosRef.current;
      const row =
        focus === 'hydros-link'
          ? (thing &&
              [...(root?.querySelectorAll('[data-hydros-thing]') || [])].find(
                (el) => el.dataset.hydrosThing === thing
              )) ||
            root?.querySelector('[data-unlinked-controller]')
          : null;
      (row || hydrosRef.current)?.scrollIntoView({
        block: row ? 'center' : 'start',
        behavior: 'smooth',
      });
      return Boolean(row);
    };
    let again = null;
    const t = setTimeout(() => {
      if (!scroll() && focus === 'hydros-link') again = setTimeout(scroll, 1200);
    }, 400);
    return () => {
      clearTimeout(t);
      clearTimeout(again);
    };
  }, [focus, linkTo]);
  // Its own "Send feedback" is on the page: no floating button over it on a
  // phone (theme.css, as the header does).
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('hdr-has-feedback');
    return () => root.classList.remove('hdr-has-feedback');
  }, []);
  const jump = (id) =>
    document.getElementById(id)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  return (
    <div className="lb-page" aria-labelledby="account-title">
      <header className="lb-page-header">
        <button type="button" className="ts-back" aria-label="Back" onClick={onClose}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          <span>Back</span>
        </button>
        <div className="ts-page-title">
          <h2 id="account-title" className="font-heading lb-title">
            Account
          </h2>
        </div>
      </header>
      <div className="lb-page-body">
        <nav className="acct-jump" aria-label="On this page">
          {ACCOUNT_SECTIONS.map(([id, label]) => (
            <button
              key={id}
              type="button"
              className="ts-chip acct-jump-chip"
              onClick={() => jump(id)}
            >
              {label}
            </button>
          ))}
        </nav>
        <div style={{ marginBottom: 12 }}>
          <ThemeToggle />
        </div>
        <AccountPanel />
        <InvitePanel />
        <div ref={hydrosRef} id="account-hydros" style={{ scrollMarginTop: 12 }}>
          <HydrosAccountPanel focus={focus === 'hydros'} linkTo={linkTo} />
        </div>
        <div id="account-ring" style={{ scrollMarginTop: 12 }}>
          <RingAccountPanel focus={focus === 'ring'} />
        </div>
        <div id="account-devices" style={{ scrollMarginTop: 12 }}>
          <DevicesAccountPanel />
        </div>
        <div className="acct-foot">
          <button
            type="button"
            className="ts-chip acct-foot-btn"
            onClick={() => window.dispatchEvent(new CustomEvent(OPEN_FEEDBACK_EVENT))}
          >
            Send feedback
          </button>
          <a href="/changelog" target="_blank" rel="noreferrer" className="acct-foot-link">
            What&apos;s new in TankSensei
          </a>
          {onSignOut && (
            <button type="button" className="ts-chip acct-foot-btn" onClick={onSignOut}>
              Sign out
            </button>
          )}
        </div>
        <div id="account-delete" style={{ scrollMarginTop: 12 }}>
          <DeleteAccountSection />
        </div>
      </div>
    </div>
  );
}
